import { Component, EventEmitter, HostListener, Inject, OnDestroy, OnInit, Output, ViewEncapsulation } from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { Router } from "@angular/router";

import { MatDialogRef, MAT_DIALOG_DATA } from "@angular/material/dialog";

import { Observable, Subscription } from "rxjs";
import { skip } from "rxjs/operators";
import { select, Store } from "@ngrx/store";
import { AppState, fuelingModeIsLoading, fuelingModeListState, loadedServiceModeIsLoadingState, loadedServiceModesState, sitesStateIsLoading } from "src/app/store/reducers";
import { getFuelingModes } from "src/app/store/actions/fueling-modes/fueling-modes.actions";
import { getServiceModes } from "src/app/store/actions/services-modes/services-mode.actions";
import { createFcc, updateFcc } from "src/app/store/actions/sites/sites.actions";

import { TranslateService } from "@ngx-translate/core";
import { SnackbarService } from "src/app/shared/services/snackbar/snackbar.service";

import { FccPmpCfgProfiles, FccType, FccTypeCode, SiteDeviceFccDTO } from "src/app/models/Sites/siteFccDTO";
import { ServiceModeDTO } from "src/app/models/service-modes/service-modesDTO";
import { FuelingModeDTO } from "src/app/models/fueling-mode/fueling-modeDTO";
import { SiteFuelPointDTO } from "src/app/models/Sites/siteFuelPointDTO";
import { SitePriceSignDTO } from "src/app/models/Sites/sitePriceSignDTO";
import { TankLevelGaugesDTO } from "src/app/models/tank-gauges/tank-gaugesDTO";
import { SitePosDTO } from "src/app/models/Sites/sitePosDTO";
import { Dialog, GuardConfirmationDialogData } from "src/app/models/dialog/dialog.model";
import { CommunicationMethodTypeCode, PROTOCOL_FORM_PARAMS } from "src/app/models/protocol/protocol.model";
import { SnackbarType } from "src/app/models/snackbar/snackbar.model";
import { isAuthenticated } from "src/app/shared/utils/authenticationUtils";
import { FormUtils } from "src/app/shared/utils/formUtils";
import { setCommunicationMethodData, setProtocolFormValues } from "src/app/shared/utils/protocolFormUtils";

import { CanComponentDeactivate } from "src/app/shared";
import { ConfirmationDialogComponent } from "src/app/shared/modules/dialogs/confirmation-dialog/confirmation-dialog.component";

const FCC_FORM_PARAMS = {
  enabled: "enabled",
  fccTypeCode: "fccTypeCode",
  pmpType: "pmpType",
  pmpTypeProfile: "pmpTypeProfile",
  protocol: "protocol",
  serialNumber: "serialNumber",
  description: "description",
  serviceModeCode: "serviceModeCode",
  fuellingModeCode: "fuellingModeCode",
  connectedFuelPoint: "connectedFuelPoint",
  connectedPriceSings: "connectedPriceSings",
  connectedTLGs: "connectedTLGs",
  connectedPOs: "connectedPOs",
};

@Component({
  selector: "app-fcc-form-dialog",
  templateUrl: "./fcc-form-dialog.component.html",
  styleUrls: ["./fcc-form-dialog.component.scss"],
  encapsulation: ViewEncapsulation.None,
})
export class FccFormDialogComponent implements OnInit, OnDestroy, CanComponentDeactivate {
  @Output() getFccForm: EventEmitter<FormGroup> = new EventEmitter<FormGroup>();

  isLoading$: Observable<boolean>;

  siteCode: string;
  currentFcc: SiteDeviceFccDTO;

  isCreate: boolean;
  fccDataForm: FormGroup;

  fccTypeList: FccType[];
  priceSignList: SitePriceSignDTO[];
  fuelPointList: SiteFuelPointDTO[];
  tlgList: TankLevelGaugesDTO[];
  posList: SitePosDTO[];

  fuelingModeSub: Subscription;
  serviceModeSub: Subscription;

  //FCC associated devices lists
  serviceModeList$: Observable<ServiceModeDTO[]>;
  serviceModeList: ServiceModeDTO[];

  fuellingModeList$: Observable<FuelingModeDTO[]>;
  fuellingModeList: FuelingModeDTO[];

  //Loadings
  isLoadingFuelingMode$: Observable<boolean>;
  isLoadingServiceMode$: Observable<boolean>;

  searchTimer: number;

  fccTypeCode = FccTypeCode;
  fccPmpCfgProfiles = FccPmpCfgProfiles;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    private dialogRef: MatDialogRef<FccFormDialogComponent>,
    private fb: FormBuilder,
    private router: Router,
    private store: Store<AppState>,
    private snackbarService: SnackbarService,
    private translate: TranslateService
  ) {
    this.isLoading$ = this.store.pipe(select(sitesStateIsLoading));
    this.isLoadingFuelingMode$ = this.store.pipe(select(fuelingModeIsLoading));
    this.isLoadingServiceMode$ = this.store.pipe(select(loadedServiceModeIsLoadingState));
    this.fuellingModeList$ = this.store.pipe(select(fuelingModeListState));
    this.serviceModeList$ = this.store.pipe(select(loadedServiceModesState));
  }

  ngOnInit(): void {
    this.setFuellingModeSearchSubscription();
    this.setServiceModeSearchSubscription();
    this.setDialogData();
    this.createFccForm();
    this.setFormValues(this.currentFcc);
  }

  setDialogData() {
    this.isCreate = this.data.isCreate;
    this.siteCode = this.data.siteCode;
    this.currentFcc = this.data.currentFcc;
    this.fccTypeList = this.data.fccTypeList;
    this.priceSignList = this.data.priceSignList
    this.fuelPointList = this.data.fuelPointList;
    this.tlgList = this.data.tlgList;
    this.posList = this.data.posList;
  
  }

  createFccForm() {
    this.fccDataForm = this.fb.group({
      [FCC_FORM_PARAMS.enabled]: [true],
      [FCC_FORM_PARAMS.serialNumber]: [null, Validators.maxLength(50)],
      [FCC_FORM_PARAMS.description]: [
        null,
        [
          Validators.required,
          FormUtils.noWhitespaceValidator,
          Validators.maxLength(45),
        ],
      ],
      [FCC_FORM_PARAMS.serviceModeCode]: [
        null,
        [Validators.required, FormUtils.isObjectValidator],
      ],
      [FCC_FORM_PARAMS.fuellingModeCode]: [
        null,
        [Validators.required, FormUtils.isObjectValidator],
      ],
      [FCC_FORM_PARAMS.connectedFuelPoint]: [null],
      [FCC_FORM_PARAMS.connectedPOs]: [null],
      [FCC_FORM_PARAMS.connectedPriceSings]: [null],
      [FCC_FORM_PARAMS.connectedTLGs]: [null],
      [FCC_FORM_PARAMS.fccTypeCode]: [null, [Validators.required]],
      [FCC_FORM_PARAMS.pmpType]: [null, [Validators.required]],
      [FCC_FORM_PARAMS.pmpTypeProfile]: [null, [Validators.required]],
      [FCC_FORM_PARAMS.protocol]: this.fb.group({}),
    });
    this.onFccTypeChanges(this.fccDataForm);
    this.onPmpTypeChanges(this.fccDataForm);
    this.onConnectedFuelPointChanges(this.fccDataForm);
    this.subscribeFormChanges(this.fccDataForm);
  }

  onFccTypeChanges(form: FormGroup): void {
    form
      .get(FCC_FORM_PARAMS.fccTypeCode)
      .valueChanges.subscribe((fccTypeCode) => {
        if (fccTypeCode && fccTypeCode === FccTypeCode.PETROTEC) {
          form.get(FCC_FORM_PARAMS.pmpType).setValidators([Validators.required]);
        } else {
          form.get(FCC_FORM_PARAMS.pmpType).clearValidators();
          form.get(FCC_FORM_PARAMS.pmpType).updateValueAndValidity();
          form.get(FCC_FORM_PARAMS.pmpType).setValue(null);
        }
      });
  }

  onPmpTypeChanges(form: FormGroup): void {
    form.get(FCC_FORM_PARAMS.pmpType).valueChanges.subscribe((pmpType) => {
      if (pmpType && pmpType === FccPmpCfgProfiles[1].id) {
        form.get(FCC_FORM_PARAMS.pmpTypeProfile).setValidators([Validators.required]);
      } else {
        form.get(FCC_FORM_PARAMS.pmpTypeProfile).clearValidators();
        form.get(FCC_FORM_PARAMS.pmpTypeProfile).updateValueAndValidity();
        form.get(FCC_FORM_PARAMS.pmpTypeProfile).setValue(null);
      }
    });
  }

  onConnectedFuelPointChanges(form: FormGroup): void {
    form.get(FCC_FORM_PARAMS.connectedFuelPoint).valueChanges.subscribe((fuelPoints) => {
      if (fuelPoints && fuelPoints.length) {
        form.get(FCC_FORM_PARAMS.fccTypeCode).disable();
        form.get(FCC_FORM_PARAMS.pmpType).disable();
        form.get(FCC_FORM_PARAMS.pmpTypeProfile).disable();
      } else {
        form.get(FCC_FORM_PARAMS.fccTypeCode).enable();
        form.get(FCC_FORM_PARAMS.pmpType).enable();
        form.get(FCC_FORM_PARAMS.pmpTypeProfile).enable();
      }
    });
  }

  subscribeFormChanges(form: FormGroup) {
    form.valueChanges.subscribe(() => {
      this.getFccFormPristine();
    });
  }

  setFuellingModeSearchSubscription() {
    this.fuelingModeSub = this.fuellingModeList$
      .pipe(skip(1))
      .subscribe((fuelingModes) => {
        if (fuelingModes) {
          this.fuellingModeList = fuelingModes.map(({ code, description }) => {
            return {
              code: code,
              description: description ?? "",
            };
          });
        }
      });
  }

  setServiceModeSearchSubscription() {
    this.serviceModeSub = this.serviceModeList$
      .pipe(skip(1))
      .subscribe((serviceModes) => {
        if (serviceModes) {
          this.serviceModeList = serviceModes.map(({ code, description }) => {
            return {
              code: code,
              description: description ?? "",
            };
          });
        }
      });
  }

  validateFirstFuelingModeSearch(event: KeyboardEvent) {
    if (!this.fuellingModeList || !this.fuellingModeList.length) {
      this.searchFuellingMode(event, false);
    }
  }

  validateFirstServiceModeSearch(event: KeyboardEvent) {
    if (!this.serviceModeList || !this.serviceModeList.length) {
      this.searchServiceMode(event, false);
    }
  }

  searchFuellingMode(event: KeyboardEvent, hasTimer = true) {
    const { value: searchTerm } = event.target as HTMLInputElement;

    clearTimeout(this.searchTimer);

    this.searchTimer = window.setTimeout(
      () => {
        this.store.dispatch(
          getFuelingModes({
            options: {
              filteringArgs: {
                filterConfig: [{ field: "description" }],
                searchTerms: [searchTerm],
              },
            },
          })
        );
      },
      hasTimer ? 500 : 0
    );
  }

  searchServiceMode(event: KeyboardEvent, hasTimer = true) {
    const { value: searchTerm } = event.target as HTMLInputElement;

    clearTimeout(this.searchTimer);

    this.searchTimer = window.setTimeout(
      () => {
        this.store.dispatch(
          getServiceModes({
            options: {
              filteringArgs: {
                filterConfig: [{ field: "description" }],
                searchTerms: [searchTerm],
              },
            },
          })
        );
      },
      hasTimer ? 500 : 0
    );
  }

  displayFuellingMode(fuellingMode: {
    code: string;
    description: string;
  }): string {
    return fuellingMode?.description ?? "";
  }

  displayServiceMode(serviceMode: {
    code: string;
    description: string;
  }): string {
    return serviceMode?.description ?? "";
  }

  checkCompatibleFuelPoint(fuelpoint: SiteFuelPointDTO): boolean {
    const selectedFuelPoints = this.fccDataForm.get(FCC_FORM_PARAMS.connectedFuelPoint).value;
    if (selectedFuelPoints && selectedFuelPoints.length) {
      if (selectedFuelPoints.find((fuelPointCode) => fuelPointCode === fuelpoint.code)) {
        return false;
      }
    }
    
    const { protocolTypeCode, communicationMethodData: { baudRate }} = fuelpoint;
    const pmpTypeId = this.fccDataForm.get(FCC_FORM_PARAMS.pmpType).value;
    const pmpTypeProfileId = this.fccDataForm.get(FCC_FORM_PARAMS.pmpTypeProfile).value;
    if (!pmpTypeId || pmpTypeId === '2' && !pmpTypeProfileId) {
      return true;
    }

    const selectedConfig = FccPmpCfgProfiles.find(({ id }) => id === pmpTypeId);
    if (protocolTypeCode === FccPmpCfgProfiles[1].code) {
      const selectedProfile = FccPmpCfgProfiles[1].profile.find((profile) => profile.baudRate === baudRate);
      return selectedConfig.code !== protocolTypeCode || selectedProfile.id !== pmpTypeProfileId;
    }

    return selectedConfig.code !== protocolTypeCode;
  }

  
  setFormValues(fcc: SiteDeviceFccDTO, duplication?: boolean) {
    this.fccDataForm.controls[FCC_FORM_PARAMS.enabled].setValue(
      fcc?.enabled ?? false
    );
    this.fccDataForm.controls[FCC_FORM_PARAMS.serialNumber].setValue(
      duplication 
      ? null 
      : fcc.serialNumber ?? null
    );
    this.fccDataForm.controls[FCC_FORM_PARAMS.description].setValue(
      fcc.description ?? null
    );
    this.fccDataForm.controls[FCC_FORM_PARAMS.serviceModeCode].setValue(
      fcc.serviceModeCode ? fcc.serviceMode : null
    );
    this.fccDataForm.controls[FCC_FORM_PARAMS.fuellingModeCode].setValue(
      fcc.fuellingModeCode ? fcc.fuellingMode : null
    );
    this.fccDataForm.controls[FCC_FORM_PARAMS.connectedFuelPoint].setValue(
      duplication 
      ? null
      : fcc.connectedFuelPoint ?? null
    );
    this.fccDataForm.controls[FCC_FORM_PARAMS.connectedPOs].setValue(
      fcc.connectedPOs ?? null
    );
    this.fccDataForm.controls[FCC_FORM_PARAMS.connectedPriceSings].setValue(
      duplication
        ? null
        : fcc.connectedPriceSings ?? null
    );
    this.fccDataForm.controls[FCC_FORM_PARAMS.connectedTLGs].setValue(
      duplication
      ? null
      : fcc.connectedTLGs ?? null
    );
    this.fccDataForm.controls[FCC_FORM_PARAMS.fccTypeCode].setValue(
      fcc.protocolTypeCode ?? null
    );
    this.fccDataForm.controls[FCC_FORM_PARAMS.pmpType].setValue(
      fcc.additionalData?.petrotecLegacyConfig?.pmpCfg?.pmpType ?? null
    );
    this.fccDataForm.controls[FCC_FORM_PARAMS.pmpTypeProfile].setValue(
      fcc.additionalData?.petrotecLegacyConfig?.pmpCfg?.pmpTypeProfile ?? null
    );

    setProtocolFormValues(this.fccDataForm, fcc);
  }

  duplicate() {
    this.setFormValues(this.currentFcc, true);

    this.isCreate = true;
    this.currentFcc = null;
  }

  onSubmit() {
    if (!this.fccDataForm.valid) {
      this.fccDataForm.markAllAsTouched();
      this.snackbarService.show({
        message: this.translate.instant("SiteManagement.formInvalid"),
        type: SnackbarType.ERROR,
      });
      return;
    }

    if (this.fccDataForm.pristine && !this.isCreate) {
      this.snackbarService.show({
        message: this.translate.instant("SiteManagement.NoChanges"),
        type: SnackbarType.WARNING,
      });
      return;
    }

    const protocolFormGroup = this.fccDataForm.get(
      FCC_FORM_PARAMS.protocol
    ) as FormGroup;

    let fcc: SiteDeviceFccDTO = {
      siteCode: this.siteCode ? this.siteCode : null,
      code: this.currentFcc?.code,
      enabled: this.fccDataForm.controls.enabled.value ?? false,
      serialNumber: this.fccDataForm.controls.serialNumber. value ?? null,
      description: this.fccDataForm.controls.description.value ?? null,
      connectedFuelPoint: this.fccDataForm.controls.connectedFuelPoint.value ?? [],
      connectedPOs: this.fccDataForm.controls.connectedPOs.value ?? [],
      connectedPriceSings: this.fccDataForm.controls.connectedPriceSings.value ?? [],
      connectedTLGs: this.fccDataForm.controls.connectedTLGs.value ?? [],
      serviceModeCode: this.fccDataForm.controls.serviceModeCode.value?.code ?? null,
      fuellingModeCode: this.fccDataForm.controls.fuellingModeCode.value?.code ?? null,
      protocolTypeCode: this.fccDataForm.controls.fccTypeCode.value ?? null,
      communicationMethodTypeCode:
        protocolFormGroup.get(PROTOCOL_FORM_PARAMS.communicationMethodTypeCode).value
          ? CommunicationMethodTypeCode.SERIAL_CONNECTION
          : CommunicationMethodTypeCode.IP_PROTOCOL,
      communicationMethodData: setCommunicationMethodData(protocolFormGroup),
      additionalData:
        this.fccDataForm.get(FCC_FORM_PARAMS.fccTypeCode).value === FccTypeCode.PETROTEC
          ? {
              petrotecLegacyConfig: {
                pmpCfg: {
                  pmpType: this.fccDataForm.controls.pmpType.value ?? null,
                  pmpTypeProfile: this.fccDataForm.controls.pmpTypeProfile.value ?? null,
                },
              },
            }
          : null,
    };

    if (this.isCreate) {
      this.store.dispatch(createFcc({ fcc }));
    } else {
      this.store.dispatch(updateFcc({ fcc }));
    }

    this.fccDataForm.markAsUntouched();
    this.fccDataForm.markAsPristine();
    this.getFccFormPristine();
  }

  getFccFormPristine() {
    this.getFccForm.emit(this.fccDataForm);
  }

  close() {
    this.dialogRef.close();
  }

  ngOnDestroy(): void {
    if (this.serviceModeSub) {
      this.serviceModeSub.unsubscribe();
    }
    if (this.fuelingModeSub) {
      this.fuelingModeSub.unsubscribe();
    }
  }

  @HostListener("window:beforeunload")
  canDeactivate(): Observable<boolean> | boolean {
    return this.isNavigationAllowed();
  }

  isNavigationAllowed(): Observable<boolean> | boolean {
    if (!isAuthenticated(this.router.url) || this.fccDataForm.pristine) {
      return true;
    } else {
      return false;
    }
  }
  
  confirmDialog(dialog: any): Observable<boolean> {
    const dialogData: Dialog = GuardConfirmationDialogData;
    const dialogRef = dialog.open(ConfirmationDialogComponent, dialogData);
    return dialogRef.afterClosed();
  }
}
